import { useCallback, useState } from 'react';
import {
  useMdiProgress,
  usePosition,
  useRunState,
  useSendCommand,
  useWorkPosition,
} from '@/store/store';
import { AXIS_CONFIG } from '@/types/machine.types';
import { HomeIcon } from '@/icons';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import styles from '@/components/DROPanel/DROPanel.module.css';

// Snap sub-micron values to a clean "0.000" so float noise / negative zero
// (e.g. -0.0001 → "-0.000") never shows on the readout.
function formatDro(value: number): string {
  return (Math.abs(value) < 0.001 ? 0 : value).toFixed(3);
}

const GOTO_BUTTONS: readonly { label: string; command: string }[] = [
  { label: 'Go XYZ', command: 'G0 X0 Y0 Z0' },
  { label: 'Go X0', command: 'G0 X0' },
  { label: 'Go Y0', command: 'G0 Y0' },
  { label: 'Go Z0', command: 'G0 Z0' },
  { label: 'Go A0', command: 'G0 A0' },
  { label: 'Go C0', command: 'G0 C0' },
];

export function DROPanel(): JSX.Element {
  const position = usePosition();
  const workPosition = useWorkPosition();
  const sendCommand = useSendCommand();
  const mdi = useMdiProgress();
  const runState = useRunState();

  // The DRO Home button is only reachable once the machine is homed (before that
  // the SafetyOverlay covers the screen), so pressing it is always a re-home —
  // confirm first since it unhomes and moves the axes through a full cycle.
  const [showHomeConfirm, setShowHomeConfirm] = useState(false);

  // Zero/Go-to fire single MDI lines. Block them while an MDI queue is pacing or
  // an AUTO program runs, so a stray G0/G10 can't interleave with active motion.
  const mdiBusy = mdi.running || runState === 'running' || runState === 'paused';

  const handleConfirmHome = useCallback(() => {
    setShowHomeConfirm(false);
    sendCommand({ type: 'HOME_ALL' });
  }, [sendCommand]);

  // Zero the work coordinate for one axis (G10 L20 P0 sets the current
  // position of that axis to 0 in the active work offset).
  const handleZero = useCallback(
    (axisName: string) => {
      sendCommand({ type: 'MDI', command: `G10 L20 P0 ${axisName}0` });
    },
    [sendCommand],
  );

  // Rapid-move the given axes to their work-coordinate zero (G0).
  const handleGoto = useCallback(
    (command: string) => {
      sendCommand({ type: 'MDI', command });
    },
    [sendCommand],
  );

  // Zero the work coordinate for all axes in a single MDI line.
  const handleZeroAll = useCallback(() => {
    sendCommand({ type: 'MDI', command: 'G10 L20 P0 X0 Y0 Z0 A0 C0' });
  }, [sendCommand]);

  // Cycle Start resumes a paused program (feedhold release).
  const handleCycleStart = useCallback(() => {
    sendCommand({ type: 'RESUME_PROGRAM' });
  }, [sendCommand]);

  // Feedhold pauses the running program.
  const handleFeedhold = useCallback(() => {
    sendCommand({ type: 'PAUSE_PROGRAM' });
  }, [sendCommand]);

  // Reset engages E-Stop (stops everything, clears the queue).
  const handleReset = useCallback(() => {
    sendCommand({ type: 'MACHINE_ESTOP' });
  }, [sendCommand]);

  // Unlock clears E-Stop and powers the machine on.
  const handleUnlock = useCallback(() => {
    sendCommand({ type: 'MACHINE_UNLOCK' });
  }, [sendCommand]);

  return (
    <div className={styles.droPanel} role="region" aria-label="Digital Readout">
      <div className={styles.droPanelInner}>
        {/* Left: axis rows */}
        <div className={styles.droPanelLeft}>
          <div className={styles.droAxes}>
            {AXIS_CONFIG.map((axis) => {
              const rawPos = position[axis.index];
              const rawWork = workPosition[axis.index];
              const machine = typeof rawPos === 'number' ? rawPos : 0;
              const work = typeof rawWork === 'number' ? rawWork : 0;
              // Primary = active work coordinate (G54…, incl. G92 + tool offsets),
              // computed backend-side. Secondary = machine absolute (G53).
              const primary = formatDro(work);
              const secondary = formatDro(machine);
              return (
                <div key={axis.name} className={styles.droAxisRow}>
                  <div className={styles.droReadout}>
                    <span className={styles.droAxisCap}>{axis.name}</span>
                    <div className={styles.droValues}>
                      <span className={styles.droAxisPrimary}>{primary}</span>
                      <span className={styles.droAxisSecondary}>{secondary}</span>
                    </div>
                  </div>
                  <button
                    className={styles.droZeroBtn}
                    type="button"
                    disabled={mdiBusy}
                    onClick={() => handleZero(axis.name)}
                    aria-label={`Zero ${axis.name} axis`}
                  >
                    Zero
                  </button>
                </div>
              );
            })}
          </div>

          {/* Go-to buttons */}
          <div className={styles.droGotoButtons}>
            {GOTO_BUTTONS.map(({ label, command }) => (
              <button
                key={label}
                className={styles.droGotoBtn}
                type="button"
                disabled={mdiBusy}
                onClick={() => handleGoto(command)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Separator */}
        <div className={styles.droSeparator} />

        {/* Right: action buttons */}
        <div className={styles.droPanelRight}>
          <button
            className={`${styles.droActionBtn} ${styles.droActionBtnHome}`}
            type="button"
            onClick={() => setShowHomeConfirm(true)}
            aria-label="Home all axes"
          >
            <HomeIcon width={20} height={20} />
            Home
          </button>
          <button
            className={styles.droActionBtn}
            type="button"
            disabled={mdiBusy}
            onClick={handleZeroAll}
            aria-label="Zero all axes"
          >
            Zero All
          </button>
          <button
            className={styles.droActionBtn}
            type="button"
            onClick={handleUnlock}
            aria-label="Unlock machine"
          >
            Unlock
          </button>
          <button
            className={styles.droActionBtn}
            type="button"
            onClick={handleCycleStart}
            aria-label="Cycle start"
          >
            Cycle Start
          </button>
          <button
            className={styles.droActionBtn}
            type="button"
            onClick={handleFeedhold}
            aria-label="Feedhold"
          >
            Feedhold
          </button>
          <button
            className={`${styles.droActionBtn} ${styles.droActionBtnPrimary}`}
            type="button"
            onClick={handleReset}
            aria-label="Reset"
          >
            Reset
          </button>
        </div>
      </div>

      {showHomeConfirm && (
        <ConfirmModal
          title="Re-Home Machine"
          icon={HomeIcon}
          headline="Machine is already homed"
          message="This will unhome all joints and run a full homing cycle — the axes will move."
          confirmLabel="Home Again"
          cancelLabel="Cancel"
          onConfirm={handleConfirmHome}
          onClose={() => setShowHomeConfirm(false)}
        />
      )}
    </div>
  );
}
